import React from 'react';

import { useLocation, useParams } from 'react-router-dom';

import {
  AutoAwesomeOutlined,
  BedOutlined,
  BuildOutlined,
  FactCheckOutlined,
  GradingOutlined,
  HotelOutlined,
  ImageOutlined,
  MapOutlined,
  MeetingRoomOutlined,
  PaidOutlined,
  PoolOutlined,
} from '@mui/icons-material';
import { Box, SxProps } from '@mui/material';

import AppSidebar from '~/components/layouts/AppSidebar';
import { SidebarItemProps } from '~/components/layouts/AppSidebar/SidebarItem';
import SidebarSection, { SidebarSectionProps } from '~/components/layouts/AppSidebar/SidebarSection';

import { useGetPropertyRooms } from '~/hooks/apis/accommodation/useGetPropertyRooms';

const SIDEBAR_STYLE: SxProps = {
  borderColor: 'divider',
  width: '100%',
  height: '100%',
  overflowY: 'auto',
};

function roomLabel(roomName: string | null | undefined, roomId: string) {
  return roomName || `Room ${roomId}`;
}

export default function AccommodationPropertyDetailsSidebar() {
  const { pathname } = useLocation();
  const { propertyId } = useParams<{ propertyId: string }>();
  const baseUrl = `/accommodation/properties/${propertyId}`;
  const {
    data: rooms = [],
    isLoading,
    error,
  } = useGetPropertyRooms({
    propertyId: propertyId ?? '',
    enabled: !!propertyId,
  });

  const item = (label: string, path: string, icon: React.ReactNode): SidebarItemProps => ({
    label,
    icon,
    url: `${baseUrl}${path}`,
    selected: pathname === `${baseUrl}${path.split('?')[0]}`,
  });

  const roomItems: Array<SidebarItemProps> = [
    item('All', '/rooms', <BedOutlined />),
    ...rooms.map((room) => ({
      label: roomLabel(room.name, room.id),
      icon: <MeetingRoomOutlined />,
      url: `${baseUrl}/rooms/${room.id}/details`,
      selected: pathname.startsWith(`${baseUrl}/rooms/${room.id}`),
    })),
  ];

  const sections: Array<SidebarSectionProps> = [
    {
      title: 'DETAILS',
      items: [
        item('Data', '/data', <FactCheckOutlined />),
        item('Generated Content', '/generated-content', <AutoAwesomeOutlined />),
        item('Content Selection', '/content', <GradingOutlined />),
      ],
    },
    {
      title: 'MAPPING',
      items: [
        item('Suppliers', '/suppliers', <HotelOutlined />),
        item('Room Mapping', '/room-mapping', <MapOutlined />),
      ],
    },
    {
      title: 'CATALOG DATA',
      items: [
        item('Facilities', '/facilities', <PoolOutlined />),
        item('Images', '/property-images', <ImageOutlined />),
      ],
    },
    {
      title: 'UTILS',
      items: [item('Rates', '/rates?useV2=true', <PaidOutlined />), item('Actions', '/actions', <BuildOutlined />)],
    },
    {
      title: 'ROOMS',
      items: roomItems,
    },
  ];

  return (
    <AppSidebar sx={SIDEBAR_STYLE}>
      {sections.map((section, index) => (
        <SidebarSection key={`${section.title}-${index}`} {...section}>
          {section.title === 'ROOMS' && isLoading && <Box sx={{ px: 1.5, py: 1 }}>Loading rooms...</Box>}
          {section.title === 'ROOMS' && error && <Box sx={{ px: 1.5, py: 1 }}>Failed to load rooms</Box>}
        </SidebarSection>
      ))}
    </AppSidebar>
  );
}
